/**
 * As-of share balances for custom reports (#6325).
 *
 * Balances are rebuilt backwards from each holder's current `owned`, reversing
 * transactions dated after the cutoff, so gaps in older ledger history can't
 * skew the result (unlike a forward replay from zero).
 */

function parseQuantity(value) {
	const n = Number(String(value ?? '').replace(/[^-.\d]/g, ''));
	return Number.isFinite(n) ? n : 0;
}

/**
 * Net change in `owned` per class and holder from transactions dated after asOfDate.
 * Double-entry, sign kept (receiver +q, sender -q), matching CapTableModule.historicRollup:
 * a negative contra row (e.g. an Alera forfeiture) moves shares back to the sender.
 * @param {Object} equity - clientData.equity
 * @param {number} asOfDate - Cutoff (Unix seconds)
 * @returns {Object} { [classKey]: Map<useridLower, delta> }
 */
export function ownedDeltasAfter(equity, asOfDate) {
	const deltas = {};
	Object.keys(equity || {}).forEach((k) => {
		const byUser = new Map();
		(equity[k]?.transactions || []).forEach((t) => {
			if (!(Number(t?.date) > asOfDate)) return;
			const q = parseQuantity(t.quantity);
			if (!q) return;
			const sender = t.sender?.toLowerCase();
			const receiver = t.receiver?.toLowerCase();
			if (sender && sender === receiver) return;
			if (receiver) byUser.set(receiver, (byUser.get(receiver) || 0) + q);
			if (sender) byUser.set(sender, (byUser.get(sender) || 0) - q);
		});
		deltas[k] = byUser;
	});
	return deltas;
}

/**
 * A current `owned` value rolled back by its delta from ownedDeltasAfter, floored at 0.
 * @param {number|string} owned
 * @param {number} delta
 * @returns {number}
 */
export function ownedAsOf(owned, delta) {
	return Math.max(0, parseQuantity(owned) - (delta || 0));
}

/**
 * Copy of userClient.shares with each class's `owned` rolled back to the cutoff.
 * @param {Object} userShares - userClient.shares
 * @param {Object} deltas - Output of ownedDeltasAfter
 * @param {string} userid
 * @returns {Object} As-of userShares (classes without later activity are shared, not copied)
 */
export function sharesAsOf(userShares, deltas, userid) {
	if (!userShares) return userShares;
	const uid = userid?.toLowerCase();
	const scoped = {};
	Object.keys(userShares).forEach((k) => {
		const classShares = userShares[k];
		const delta = deltas[k]?.get(uid) || 0;
		if (!delta || !classShares || typeof classShares !== 'object') {
			scoped[k] = classShares;
			return;
		}
		scoped[k] = { ...classShares, owned: ownedAsOf(classShares.owned, delta) };
	});
	return scoped;
}

/**
 * Cap table with every holder's shares rolled back to the cutoff.
 * @param {Array} capTableData
 * @param {string} client - Client slug (key into user.clients)
 * @param {Object} deltas - Output of ownedDeltasAfter
 * @returns {Array}
 */
export function capTableAsOf(capTableData, client, deltas) {
	return capTableData.map((user) => {
		const userClient = user?.clients?.[client];
		if (!userClient?.shares) return user;
		const shares = sharesAsOf(userClient.shares, deltas, user.userid);
		return { ...user, clients: { ...user.clients, [client]: { ...userClient, shares } } };
	});
}

/**
 * Memoized as-of view for one date. `cache` is a Map shared across a report run's
 * row contexts, so the rebuild runs once per date instead of once per row.
 * @param {Map} cache
 * @param {Object} source - { equity, capTableData, client }
 * @param {number} asOfDate - Cutoff (Unix seconds)
 * @returns {{ deltas: Object, capTableData: Array|undefined }}
 */
export function getAsOfView(cache, { equity, capTableData, client }, asOfDate) {
	if (!cache.has(asOfDate)) {
		const deltas = ownedDeltasAfter(equity, asOfDate);
		cache.set(asOfDate, {
			deltas,
			capTableData: Array.isArray(capTableData) ? capTableAsOf(capTableData, client, deltas) : capTableData,
		});
	}
	return cache.get(asOfDate);
}
